import { Box, Divider } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import Flex from "@/components/Flex";
import Input from "@/components/Input";
import Text from "@/components/Text";
import { useDebounce } from "@/hooks/use-debounce";
import useWebSocket from "@/hooks/use-web-socket";
// import { useSnackbar } from "@/hooks/useSnackbar";
// import { BASE_PATH } from "@/services/base";
import { useOutletContext } from "react-router-dom";
import { SegmentTabs } from "@/components/SegmentTabs";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useChatGetAll } from "@/queries/communication/chat/chatting/use-chat-get-all";
import { useGetAllConversation } from "@/queries/communication/chat/convo/use-get-all-conversation";
import { BASE_PATH } from "@/services/base";
import { formatDate } from "@/utils/date-time-utility";
import { sanitizeMessage } from "@/utils/sanitize-html";
import { DEF_PAGINATION, getXtenantId } from "@/utils/utils";
import { ChatContainer } from "./ChatContainer";
import { ChatSideBar } from "./ChatSideBar";
import { NewChatPopOver } from "./NewChatPopOver";
import { CHAT_TABS, ConvoTab } from "./chat-constant";
import selectUserImg from "/default-images/select-user.svg";
import { getViewAs } from "@/utils/auth-storage";

// Chat API returns date groups and the messages inside each group newest-first
// (descending), but the chat UI renders top-to-bottom oldest-first (like the SMS
// view). Normalise to ascending on ingest so history matches the bottom-appended
// live/sent messages.
const sortChatMessagesAsc = (data: Record<string, any[]>) =>
  Object.keys(data ?? {})
    .sort()
    .reduce<Record<string, any[]>>((acc, date) => {
      acc[date] = [...(data[date] ?? [])].sort(
        (a, b) =>
          new Date(a?.sendDate).getTime() - new Date(b?.sendDate).getTime()
      );
      return acc;
    }, {});

const Chat = () => {
  const context = useOutletContext();
  const setChatFcmCount = (context as any)?.setChatFcmCount;
  const isPatientSide = getViewAs() === "patient";
  const [activeTab, setActiveTab] = useState<ConvoTab>(
    isPatientSide ? "patient" : "provider"
  );
  const [searchKey, setSearchKey, instantSearchKey] = useDebounce<string>("");
  const [curConvo, setCurConvo] = useState<any | null>(null);
  const [me] = useCurrentUser();
  const queryClient = useQueryClient();
  const {
    data: conversationList,
    isLoading,
    refetch: refetchConvo,
  } = useGetAllConversation({
    pagination: DEF_PAGINATION,
    providerId: me?.uuid,
    xTENANTID: getXtenantId(),
    searchKey: searchKey,
    participant2Provider: isPatientSide ? true : activeTab === "provider",
  });

  const { data: chatList, dataUpdatedAt } = useChatGetAll({
    // todo: need to change adding default page size to 100
    pagination: { page: 0, pageSize: 100 },
    xTENANTID: getXtenantId(),
    conversationId: curConvo?.uuid,
  });

  useEffect(() => {
    if (chatList?.data) {
      setMessages(() => sortChatMessagesAsc(chatList?.data as any) as any);
    }
  }, [dataUpdatedAt]);

  const getChatMessage = (
    message: string,
    type: "CHAT_MESSAGE" | "RESET_COUNT" = "CHAT_MESSAGE",
    convoId?: string,
    attachment?: string | null,
    attachmentExt?: string | null,
    fileName?: string | null
  ): ChatMessage => {
    return {
      message: "ecare-chat",
      type: type,
      conversationId: convoId || curConvo?.uuid,
      data: {
        sendDate: new Date().toISOString(),
        conversation: convoId || curConvo?.uuid,
        sender: me?.uuid || "",
        // must need to identify the sender and receiver
        sendBy: me?.uuid || "",
        content: message,
        attachment: attachment || null,
        attachment_ext: attachmentExt || null,
        attachment_name: fileName || null,
        viewed: false,
      },
    };
  };

  const basePath = BASE_PATH?.split("//")?.[1];

  const enableRef = useRef(false);
  // const [_count, setFcmCount] = useFcmCounts<number>("chat");
  //                     string->date(YYYY-MM-DD)
  type Messages = Record<string, Message[]>;
  const [messages, setMessages] = useState<Messages | null>(null);

  // useEffect(() => {
  //   console.log("val here check counts messages", _count);
  // }, [messages, _count]);

  const initResetCountMessage = (convoId?: string) => {
    sendData(
      JSON.stringify(
        getChatMessage("ecare-chat", "RESET_COUNT", convoId) as any
      )
    );
  };

  const handleMessageReceived = (event: MessageEvent<any>) => {
    const chatData = JSON.parse(event?.data || "{}");

    const curDate = formatDate(chatData?.data?.sendDate, "YYYY-MM-DD") ?? "";

    if (chatData?.type === "RESET_COUNT") {
      setChatFcmCount?.("chat", chatData?.unread_count ?? 0);
      refetchConvo();
      // The reply always sends unread_count 0; the tab badge needs the total across conversations.
      queryClient.invalidateQueries({ queryKey: ["chat-unread-count"] });
    } else if (chatData?.type === "CONNECTION_ESTABLISHED") {
      initResetCountMessage(chatData?.conversationId);
    } else if (chatData?.type === "ERROR") {
      setMessages((prev) => {
        return {
          ...prev,
          [curDate]: [
            ...(prev?.[curDate] || []),
            { ...chatData?.data, isError: true },
          ],
        };
      });
    } else if (chatData?.type === "CHAT_MESSAGE") {
      setMessages((prev) => {
        return {
          ...prev,
          [curDate]: [...(prev?.[curDate] || []), chatData?.data],
        };
      });
      initResetCountMessage(chatData?.conversationId);
    }
  };

  const {
    reconnect: establishWebConnection,
    closeConnection,
    sendData,
  } = useWebSocket<any>({
    url: `wss://${basePath}/api/master/chats/${
      curConvo?.uuid
    }?tenant=${getXtenantId()}`,
    // todo: need to remove this once fully tested
    // url: "wss://6ca699bf0321.ngrok-free.app/api/master/chats/123e4567-e89b-12d3-a456-426614174000?tenant=local_mypg",
    onMessageReceived: handleMessageReceived,
    disableInitialConnection: !enableRef.current,
  });

  const onSend = (
    msg: string,
    attachment?: string | null,
    attachmentExt?: string | null,
    fileName?: string | null
  ) => {
    // Restrict the injection-prone characters < > ' # /> (and strip dangerous
    // tags such as <a href> phishing anchors / <script>) before the message is
    // relayed over the websocket to other clients. Safe formatting is kept.
    const safeMsg = sanitizeMessage(msg ?? "");

    if (safeMsg?.trim() || attachment) {
      const curMessageDate =
        formatDate(new Date().toISOString(), undefined, "YYYY-MM-DD") ?? "";

      const chatMessage = getChatMessage(
        safeMsg,
        "CHAT_MESSAGE",
        undefined,
        attachment,
        attachmentExt,
        fileName
      );
      sendData(JSON.stringify(chatMessage as any));

      setMessages((prev) => {
        return {
          ...prev,
          [curMessageDate]: [
            ...(prev?.[curMessageDate] || []),
            chatMessage?.data,
          ],
        };
      });
    }

    return new Promise((res) => {
      return res(true);
    });
  };

  const handleCurrentUserChange = (convo: any) => {
    if (convo?.uuid === curConvo?.uuid) return;

    enableRef.current = true;
    closeConnection();
    establishWebConnection();
    setCurConvo(convo);
    setMessages(null);
  };

  return (
    <Flex height={"calc(100svh - 11.5rem)"} width={"100%"}>
      <Flex p={1.2} gap={1} flexDirection={"column"}>
        {/* {<Text>Chat Count: {counts}</Text>} */}
        <Flex
          gap={1}
          width={"100%"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Box width={"100%"}>
            <Input
              textFieldProps={{
                value: instantSearchKey,
                onChange: (e) => setSearchKey(e.target.value),
                placeholder: "Search Conversation",
              }}
            />
          </Box>

          {/* ECRH-5033: patient-initiated chat disabled — chats are provider-initiated only */}
          {/* {isPatientSide && (
            <NewChatPopOver convoOnlyWithProvider={isPatientSide} />
          )} */}
        </Flex>

        {!isPatientSide && (
          <Flex
            gap={1}
            width={"100%"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <SegmentTabs
              tabsData={CHAT_TABS}
              value={activeTab}
              onChange={(tab) => {
                setActiveTab(tab as ConvoTab);
                setCurConvo(null);
                setSearchKey("");
              }}
            />
            <NewChatPopOver convoOnlyWithProvider={isPatientSide} />
          </Flex>
        )}

        <ChatSideBar
          convoList={conversationList?.data?.content}
          isLoading={isLoading}
          onCurrentUserChange={handleCurrentUserChange}
          searchKey={searchKey}
          selectedUser={curConvo}
        />
      </Flex>

      <Divider sx={{ width: "1px", backgroundColor: "gray" }} />

      {curConvo ? (
        <ChatContainer
          user={curConvo}
          onSend={onSend}
          messages={messages ?? undefined}
        />
      ) : (
        <Flex height={"100%"} width={"100%"}>
          <SelectUserToChatMsg />
        </Flex>
      )}
    </Flex>
  );
};

export default Chat;

const SelectUserToChatMsg = () => {
  return (
    <Flex
      flexDirection={"column"}
      height={"inherit"}
      width={"inherit"}
      alignItems={"center"}
      justifyContent={"center"}
      gap={2}
    >
      <img src={selectUserImg} alt={"No Message Available"} />
      <Text fontSize={"16px"}>Welcome to the Chat Space!</Text>
      <Text variant="subtitle2" fontSize={"16px"}>
        Start a New Healing Conversation
      </Text>
    </Flex>
  );
};

export type Message = {
  conversation: string;
  sender: string;
  content: string;
  attachment: string | null;
  attachment_ext: string | null;
  viewed: boolean;
};

type ChatMessage = {
  message: string;
  type: string;
  conversationId: string;
  data: {
    sendDate: string;
    conversation: string;
    sender: string;
    sendBy: string;
    content: string;
    attachment: string | null;
    attachment_ext: string | null;
    attachment_name: string | null;
    viewed: boolean;
  };
};
